import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatApiError } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Activity, Download, ArrowLeft, Loader2, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function severityColor(s) {
  if (s === 'severe') return 'bg-[#8B2626] text-white';
  if (s === 'moderate') return 'bg-[#B45309] text-white';
  if (s === 'mild') return 'bg-amber-100 text-amber-800';
  return 'bg-stone-100 text-stone-600';
}

function probColor(p) {
  if (p >= 0.7) return '#8B2626';
  if (p >= 0.4) return '#B45309';
  return '#1A4D2E';
}

export default function AnalysisPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [showBbox, setShowBbox] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showLungs, setShowLungs] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [imgAspect, setImgAspect] = useState(null);
  const [error, setError] = useState('');

  const fetchCase = async () => {
    try {
      const { data } = await api.get(`/cases/${id}`);
      setCaseData(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCase(); }, [id]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    setError('');
    try {
      const { data } = await api.post(`/cases/${id}/analyze`);
      setCaseData(data);
      toast.success('Analysis complete');
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('No credits remaining. Redirecting to subscription page...');
        navigate(`/subscription?returnTo=/case/${id}`);
        return;
      }
      setError(formatApiError(err.response?.data?.detail));
      toast.error('Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const response = await api.get(`/cases/${id}/report`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `xray_report_${id.slice(0, 8)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch {
      toast.error('Failed to download report');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></div>;
  if (error && !caseData) return <div className="text-center py-12 text-[#8B2626]">{error}</div>;
  if (!caseData) return null;

  const analysis = caseData.analysis;
  const lungs = caseData.lung_regions;
  const rawType = caseData.case_type || 'chest_xray';
  const caseType = rawType === 'bone_fracture' ? 'extremity' : rawType;
  const isExtremity = caseType === 'extremity';
  const isAbdominal = caseType === 'abdominal';
  const isChest = !(isExtremity || isAbdominal);
  const typeMeta = isExtremity
    ? { label: 'Extremity X-Ray', badgeClass: 'bg-amber-500 text-white' }
    : isAbdominal
      ? { label: 'Abdominal X-Ray', badgeClass: 'bg-blue-500 text-white' }
      : { label: 'Chest X-Ray', badgeClass: 'bg-[#1A4D2E] text-white' };
  const xrayUrl = `${API_URL}/api/uploads/${caseData.xray_filename}`;

  return (
    <div className="space-y-6" data-testid="analysis-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-heading font-medium tracking-tight text-stone-900">{caseData.patient_name}</h1>
              <Badge className={typeMeta.badgeClass} data-testid="case-type-badge">
                {typeMeta.label}
              </Badge>
            </div>
            <p className="text-sm text-stone-500">
              {caseData.patient_id && <span className="font-mono text-[#1A4D2E] mr-2">{caseData.patient_id}</span>}
              {caseData.patient_age}y, {caseData.patient_gender} — {new Date(caseData.created_at).toLocaleDateString()}
              {isExtremity && caseData.body_part && <span className="ml-2">· {caseData.body_part}</span>}
              {isAbdominal && caseData.clinical_query && <span className="ml-2">· {caseData.clinical_query}</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {!analysis && (
            <Button data-testid="analyze-btn" onClick={runAnalysis} disabled={analyzing} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white gap-2 rounded-lg">
              {analyzing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</> : <><Activity className="w-4 h-4" /> Run AI Analysis</>}
            </Button>
          )}
          {analysis && (
            <Button data-testid="download-pdf-btn" onClick={downloadPdf} variant="outline" className="gap-2 border-stone-200 rounded-lg">
              <Download className="w-4 h-4" /> Download PDF
            </Button>
          )}
        </div>
      </div>

      {error && <div className="text-sm text-[#8B2626] bg-red-50 p-3 rounded-lg">{error}</div>}

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
        <span><strong>Decision Support Only</strong> — AI findings require clinical verification. This tool does not replace professional medical diagnosis.</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* X-ray Viewer */}
        <div className="lg:col-span-3">
          <Card className="border-stone-200 shadow-sm overflow-hidden">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading">X-Ray Viewer</CardTitle>
              {analysis && (
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch data-testid="bbox-toggle" checked={showBbox} onCheckedChange={setShowBbox} />
                    <Label className="text-xs text-stone-500 cursor-pointer">Bounding Boxes</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch data-testid="heatmap-toggle" checked={showHeatmap} onCheckedChange={setShowHeatmap} />
                    <Label className="text-xs text-stone-500 cursor-pointer">Grad-CAM</Label>
                  </div>
                  {isChest && (
                    <div className="flex items-center gap-2">
                      <Switch data-testid="lung-toggle" checked={showLungs} onCheckedChange={setShowLungs} />
                      <Label className="text-xs text-stone-500 cursor-pointer">Lung Regions</Label>
                    </div>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="p-2">
              <div
                data-testid="analysis-viewer"
                className="bg-stone-950 rounded-xl border border-stone-800 overflow-hidden relative w-full"
                style={imgAspect ? { aspectRatio: String(imgAspect) } : { aspectRatio: '4 / 3' }}
              >
                {!imgError ? (
                  <img
                    src={xrayUrl}
                    alt={typeMeta.label}
                    onLoad={(e) => {
                      const el = e.target;
                      if (el.naturalWidth && el.naturalHeight) {
                        setImgAspect(el.naturalWidth / el.naturalHeight);
                      }
                    }}
                    onError={() => setImgError(true)}
                    className="absolute inset-0 w-full h-full object-contain"
                    crossOrigin="anonymous"
                  />
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-stone-400 p-6 text-center" data-testid="image-unavailable">
                    <AlertTriangle className="w-10 h-10 mb-3 text-amber-400" />
                    <p className="text-sm font-medium text-stone-200">Original X-ray image unavailable</p>
                    <p className="text-xs text-stone-500 mt-1 max-w-xs">This case was uploaded before persistent storage was enabled. Analysis findings below are preserved.</p>
                  </div>
                )}

                {/* Bounding boxes */}
                {!imgError && showBbox && analysis?.diseases?.map((d, i) => d.region && (
                  <div key={`bbox-${i}`} className="absolute border-[1.5px] sm:border-2 pointer-events-none rounded-sm" style={{
                    left: `${d.region.x * 100}%`, top: `${d.region.y * 100}%`,
                    width: `${d.region.width * 100}%`, height: `${d.region.height * 100}%`,
                    borderColor: probColor(d.probability),
                    boxShadow: `0 0 0 1px rgba(0,0,0,0.35), inset 0 0 12px ${probColor(d.probability)}22`,
                  }}>
                    <span className="absolute -top-4 left-0 text-[9px] sm:text-[10px] font-medium px-1 py-[1px] rounded whitespace-nowrap" style={{ backgroundColor: probColor(d.probability), color: '#fff' }}>
                      {d.name} {(d.probability * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}

                {/* Heatmap overlays — luminous hot palette */}
                {!imgError && showHeatmap && analysis?.diseases?.map((d, i) => d.region && (
                  <div key={`heat-${i}`} className="absolute pointer-events-none rounded-full" style={{
                    left: `${(d.region.x + d.region.width / 2) * 100}%`,
                    top: `${(d.region.y + d.region.height / 2) * 100}%`,
                    width: `${Math.max(d.region.width, d.region.height) * 100 * 0.9}%`,
                    aspectRatio: '1 / 1',
                    transform: 'translate(-50%, -50%)',
                    background: `radial-gradient(circle,
                      rgba(255,255,180,${0.55 * d.probability}) 0%,
                      rgba(255,220,60,${0.50 * d.probability}) 18%,
                      rgba(255,140,20,${0.45 * d.probability}) 40%,
                      rgba(220,40,20,${0.30 * d.probability}) 65%,
                      transparent 85%)`,
                    mixBlendMode: 'screen',
                    filter: 'blur(4px)',
                  }} />
                ))}

                {/* Lung regions (chest only) */}
                {!imgError && isChest && showLungs && lungs?.right_lung && (
                  <div className="absolute border-2 border-dashed border-cyan-400 pointer-events-none" style={{
                    left: `${lungs.right_lung.x * 100}%`, top: `${lungs.right_lung.y * 100}%`,
                    width: `${lungs.right_lung.width * 100}%`, height: `${lungs.right_lung.height * 100}%`,
                  }}>
                    <span className="absolute -top-4 left-0 text-[10px] text-cyan-400 bg-black/50 px-1 rounded">R Lung</span>
                  </div>
                )}
                {!imgError && isChest && showLungs && lungs?.left_lung && (
                  <div className="absolute border-2 border-dashed border-cyan-400 pointer-events-none" style={{
                    left: `${lungs.left_lung.x * 100}%`, top: `${lungs.left_lung.y * 100}%`,
                    width: `${lungs.left_lung.width * 100}%`, height: `${lungs.left_lung.height * 100}%`,
                  }}>
                    <span className="absolute -top-4 left-0 text-[10px] text-cyan-400 bg-black/50 px-1 rounded">L Lung</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Clinical Notes */}
          {caseData.clinical_notes && (
            <Card className="border-stone-200 shadow-sm mt-4">
              <CardContent className="pt-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-2">Clinical Notes</p>
                <p className="text-sm text-stone-700">{caseData.clinical_notes}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Results Sidebar */}
        <div className="lg:col-span-2 space-y-4">
          {!analysis ? (
            <Card className="border-stone-200 shadow-sm">
              <CardContent className="pt-6 text-center py-12">
                <Eye className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 mb-4">Run AI analysis to see diagnostic results</p>
                <Button data-testid="analyze-btn-sidebar" onClick={runAnalysis} disabled={analyzing} className="bg-[#1A4D2E] hover:bg-[#143B23] text-white">
                  {analyzing ? 'Analyzing...' : 'Analyze X-Ray'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Disease findings */}
              <Card className="border-stone-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-heading">
                    {isExtremity ? 'Skeletal Findings' : isAbdominal ? 'Abdominal Findings' : 'Findings'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div data-testid="disease-list" className="space-y-4">
                    {analysis.diseases?.map((d, i) => (
                      <div key={i} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-stone-900">{d.name}</span>
                          <div className="flex items-center gap-2">
                            <Badge className={severityColor(d.severity)}>{d.severity}</Badge>
                            <span className="text-sm font-mono" style={{ color: probColor(d.probability) }}>
                              {(d.probability * 100).toFixed(1)}%
                            </span>
                          </div>
                        </div>
                        <Progress value={d.probability * 100} className="h-2 bg-stone-100" style={{ '--progress-color': probColor(d.probability) }} />
                        <p className="text-xs text-stone-500 leading-relaxed">{d.description}</p>
                        {isExtremity && (d.fracture_type || d.location || d.displacement || d.alignment || d.joint_involvement) && (
                          <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`fracture-details-${i}`}>
                            {d.fracture_type && d.fracture_type !== 'none' && (
                              <span className="text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">Type: {d.fracture_type}</span>
                            )}
                            {d.location && (
                              <span className="text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full bg-stone-50 text-stone-700 border border-stone-200">Loc: {d.location}</span>
                            )}
                            {d.displacement && (
                              <span className="text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full bg-stone-50 text-stone-700 border border-stone-200">Displ: {d.displacement}</span>
                            )}
                            {d.alignment && (
                              <span className="text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full bg-stone-50 text-stone-700 border border-stone-200">Align: {d.alignment}</span>
                            )}
                            {d.joint_involvement && (
                              <span className="text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full bg-stone-50 text-stone-700 border border-stone-200">Joint: {d.joint_involvement}</span>
                            )}
                          </div>
                        )}
                        {isAbdominal && (d.location || d.urgency) && (
                          <div className="flex flex-wrap gap-1.5 pt-1" data-testid={`abdominal-details-${i}`}>
                            {d.location && (
                              <span className="text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full bg-stone-50 text-stone-700 border border-stone-200">Loc: {d.location}</span>
                            )}
                            {d.urgency && (
                              <span className={`text-[10px] font-mono uppercase tracking-wide px-2 py-0.5 rounded-full border ${d.urgency === 'emergency' ? 'bg-red-50 text-red-800 border-red-300' : d.urgency === 'urgent' ? 'bg-amber-50 text-amber-800 border-amber-300' : 'bg-stone-50 text-stone-700 border-stone-200'}`}>Urgency: {d.urgency}</span>
                            )}
                          </div>
                        )}
                        {i < analysis.diseases.length - 1 && <Separator className="mt-3" />}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Case-specific extras */}
              {isExtremity && (analysis.body_part_observed || analysis.soft_tissue_findings) && (
                <Card className="border-stone-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading">Additional Observations</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {analysis.body_part_observed && (
                      <div><span className="text-xs uppercase tracking-wider text-stone-500">Body part observed: </span><span className="text-stone-700">{analysis.body_part_observed}</span></div>
                    )}
                    {analysis.soft_tissue_findings && (
                      <div><span className="text-xs uppercase tracking-wider text-stone-500">Soft tissue: </span><span className="text-stone-700">{analysis.soft_tissue_findings}</span></div>
                    )}
                  </CardContent>
                </Card>
              )}
              {isAbdominal && (analysis.view_type || analysis.bowel_gas_pattern || analysis.free_air || analysis.calcifications) && (
                <Card className="border-stone-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading">Additional Observations</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {analysis.view_type && (
                      <div><span className="text-xs uppercase tracking-wider text-stone-500">View type: </span><span className="text-stone-700">{analysis.view_type}</span></div>
                    )}
                    {analysis.bowel_gas_pattern && (
                      <div><span className="text-xs uppercase tracking-wider text-stone-500">Bowel gas pattern: </span><span className="text-stone-700">{analysis.bowel_gas_pattern}</span></div>
                    )}
                    {analysis.free_air && (
                      <div><span className="text-xs uppercase tracking-wider text-stone-500">Free air: </span><span className={analysis.free_air === 'present' ? 'text-red-700 font-semibold' : 'text-stone-700'}>{analysis.free_air}</span></div>
                    )}
                    {analysis.calcifications && (
                      <div><span className="text-xs uppercase tracking-wider text-stone-500">Calcifications: </span><span className="text-stone-700">{analysis.calcifications}</span></div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Summary */}
              <Card className="border-stone-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-heading">Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-stone-700 leading-relaxed">{analysis.summary}</p>
                </CardContent>
              </Card>

              {/* Recommendations */}
              <Card className="border-stone-200 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-heading">Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-stone-700 leading-relaxed">{analysis.recommendations}</p>
                </CardContent>
              </Card>

              {/* Quality */}
              {analysis.technical_quality && (
                <Card className="border-stone-200 shadow-sm">
                  <CardContent className="pt-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-stone-500 mb-1">Image Quality</p>
                    <Badge variant="outline" className="capitalize">{analysis.technical_quality}</Badge>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
